import React, { useState } from 'react';
import { ApolloQueryResult, useLazyQuery, useMutation } from '@apollo/client';
import { Checkbox, InputNumber, Modal, Tooltip } from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  FlagOutlined,
  LockOutlined,
  ProfileOutlined,
  UnlockOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
// Helpers
import getEnvVariables from 'api/env';
// Routes
import { CAMPAIGN_DETAILS_GEN } from 'constants/routes';
// Api
import { GET_CAMPAIGN_LOCK } from 'api/campaignV2/queries';
import {
  APPROVE_CAMPAIGN,
  APPROVE_DIRECT_BOOKING_CAMPAIGN,
  APPROVE_SOCIAL_BOOST_CAMPAIGN,
  DECLINE_CAMPAIGN,
  COMPLETE_CAMPAIGN,
  EDIT_CAMPAIGN_SETTINGS,
  DELETE_CAMPAIGN,
  TAKE_CAMPAIGN_LOCK,
  RELEASE_CAMPAIGN_LOCK,
  UNWAIVE_CAMPAIGN_FEE,
  DUPLICATE_CAMPAIGN,
} from 'api/campaignV2/mutations';
// Types
import {
  ReleaseCampaignLock,
  ReleaseCampaignLockVariables,
} from 'api/campaignV2/types/ReleaseCampaignLock';
import {
  TakeCampaignLock,
  TakeCampaignLockVariables,
} from 'api/campaignV2/types/TakeCampaignLock';
import {
  GetCampaignLock,
  GetCampaignLockVariables,
} from 'api/campaignV2/types/GetCampaignLock';
import {
  DeleteCampaign,
  DeleteCampaignVariables,
} from 'api/campaignV2/types/DeleteCampaign';
import {
  DeclineCampaign,
  DeclineCampaignVariables,
} from 'api/campaignV2/types/DeclineCampaign';
import {
  ApproveCampaign,
  ApproveCampaignVariables,
} from 'api/campaignV2/types/ApproveCampaign';
import {
  ApproveDirectBookingCampaign,
  ApproveDirectBookingCampaignVariables,
} from 'api/campaignV2/types/ApproveDirectBookingCampaign';
import {
  CompleteCampaign,
  CompleteCampaignVariables,
} from 'api/campaignV2/types/CompleteCampaign';
import {
  GetCampaignsV2,
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2Variables,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  EditCampaignSettings,
  EditCampaignSettingsVariables,
} from 'api/campaignV2/types/EditCampaignSettings';
import {
  UnwaiveLaunchFee,
  UnwaiveLaunchFeeVariables,
} from 'api/campaignV2/types/UnwaiveLaunchFee';
import {
  DuplicateCampaign,
  DuplicateCampaignVariables,
} from 'api/campaignV2/types/DuplicateCampaign';
import { CampaignStatus, CampaignType } from 'api/graphql-global-types';
// Ui
import SocialBoostCampaignEditModal from '../SocialBoostCampaignEditModal/SocialBoostCampaignEditModal';
import SocialShareCampaignEditModal from '../SocialShareCampaignEditModal/SocialShareCampaignEditModal';
import { errorNotification, successNotification } from 'ui/Notification';
import { humanizeDatesInMessage } from 'helpers/campaignSocialBoost';
import { requiresAgreementSignatory } from 'helpers/campaign';
// Components
import SocialCampaignsEditModal from '../SocialCampaignsEditModal/SocialCampaignsEditModal';
import SponsoredAppearanceCampaignEditModal from '../SponsoredAppearanceCampaignEditModal/SponsoredAppearanceCampaignEditModal';
import BookMeCampaignsEditModal from '../BookMeCampaignsEditModal/BookMeCampaignsEditModal';
import EventMarketingDetailsModal from '../EventMarketingDetailsModal/EventMarketingDetailsModal';
import EventMarketingEditModal from '../EventMarketingEditModal/EventMarketingEditModal';
import UgcCampaignsEditModal from '../UgcCampaignsEditModal/UgcCampaignsEditModal';
import CustomCampaignsEditModal from '../CustomCampaignsEditModal/CustomCampaignsEditModal';
import ChangeOwnershipModal from '../ChangeOwnershipModal/ChangeOwnershipModal';
import EditDeadlineModal from '../EditDeadlineModal/EditDeadlineModal';
import ApproveWithSignatoryModal from '../ApproveWithSignatoryModal/ApproveWithSignatoryModal';
// Styles
import RowActionsMenu, { RowMenuAction } from 'ui/RowActions/RowActionsMenu';

const { confirm } = Modal;

type CampaignsActionsProps = {
  data: GetCampaignsV2_getCampaigns_entities;
  refetchCampaigns: (
    variables?: Partial<GetCampaignsV2Variables> | undefined
  ) => Promise<ApolloQueryResult<GetCampaignsV2>>;
};

const CampaignsActions = ({
  data,
  refetchCampaigns,
}: CampaignsActionsProps): JSX.Element => {
  const history = useHistory();

  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [modalKey, setModalKey] = useState<string>('');
  const [showPricePerShareModal, setShowPricePerShareModal] =
    useState<boolean>(false);
  const [pricePerShare, setPricePerShare] = useState<number | null>(null);
  const [showOwnershipModal, setShowOwnershipModal] = useState<boolean>(false);
  const [showEditDeadlineModal, setShowEditDeadlineModal] =
    useState<boolean>(false);
  const [showSignatoryModal, setShowSignatoryModal] = useState<boolean>(false);

  const [getCampaignLock, { data: campaignLock }] = useLazyQuery<
    GetCampaignLock,
    GetCampaignLockVariables
  >(GET_CAMPAIGN_LOCK, {
    variables: {
      input: {
        id: data?.id,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const [approveCampaign, { loading: loadingApprove }] = useMutation<
    ApproveCampaign,
    ApproveCampaignVariables
  >(APPROVE_CAMPAIGN, {
    onCompleted: () => {
      refetchCampaigns();
    },
  });

  const [
    approveDirectBookingCampaign,
    { loading: loadingApproveDirectBooking },
  ] = useMutation<
    ApproveDirectBookingCampaign,
    ApproveDirectBookingCampaignVariables
  >(APPROVE_DIRECT_BOOKING_CAMPAIGN, {
    onCompleted: () => {
      refetchCampaigns();
    },
  });

  const [approveSocialBoostCampaign, { loading: loadingApproveSocialBoost }] =
    useMutation(APPROVE_SOCIAL_BOOST_CAMPAIGN, {
      onCompleted: () => {
        refetchCampaigns();
      },
    });

  const [deleteCampaign] = useMutation<DeleteCampaign, DeleteCampaignVariables>(
    DELETE_CAMPAIGN
  );

  const [takeCampaignLock] = useMutation<
    TakeCampaignLock,
    TakeCampaignLockVariables
  >(TAKE_CAMPAIGN_LOCK);

  const [releaseCampaignLock] = useMutation<
    ReleaseCampaignLock,
    ReleaseCampaignLockVariables
  >(RELEASE_CAMPAIGN_LOCK);

  const [declineCampaign, { loading: loadingDecline }] = useMutation<
    DeclineCampaign,
    DeclineCampaignVariables
  >(DECLINE_CAMPAIGN, {
    onCompleted: () => {
      refetchCampaigns();
    },
  });

  const [completeCampaign, { loading: loadingComplete }] = useMutation<
    CompleteCampaign,
    CompleteCampaignVariables
  >(COMPLETE_CAMPAIGN);

  const [editCampaignSettings, { loading: loadingEditCampaignSettings }] =
    useMutation<EditCampaignSettings, EditCampaignSettingsVariables>(
      EDIT_CAMPAIGN_SETTINGS
    );

  const [unwaiveLaunchFee, { loading: loadingUnwaiveLaunchFee }] = useMutation<
    UnwaiveLaunchFee,
    UnwaiveLaunchFeeVariables
  >(UNWAIVE_CAMPAIGN_FEE);

  const [duplicateCampaign, { loading: loadingDuplicate }] = useMutation<
    DuplicateCampaign,
    DuplicateCampaignVariables
  >(DUPLICATE_CAMPAIGN, {
    onCompleted: () => {
      refetchCampaigns();
    },
  });

  const handleOpenCampaignDetails = () => {
    history.push(`${CAMPAIGN_DETAILS_GEN}/${data.id}`);
  };

  const handleOpenDetailsModal = () => {
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
  };

  const handleOpenEditCampaignModal = async () => {
    setShowEditModal(true);

    try {
      await takeCampaignLock({
        variables: {
          input: {
            id: data.id,
          },
        },
      });

      successNotification('Lock success!');
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Error locking campaign:', error);
    }
  };

  const handleReleaseCampaignLock = async () => {
    setShowEditModal(false);

    try {
      await releaseCampaignLock({
        variables: {
          input: {
            id: data.id,
          },
        },
      });

      successNotification('Unlock success!');
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Error unlocking campaign:', error);
    }
  };

  const handleCloseEditCampaignModal = async () => {
    setShowEditModal(false);
    setModalKey(Date.now().toString());

    try {
      await releaseCampaignLock({
        variables: {
          input: {
            id: data.id,
          },
        },
      });

      successNotification('Unlock success!');
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Error unlocking campaign:', error);
    }
  };

  const handleOpenOwnershipModal = () => {
    setShowOwnershipModal(true);
  };

  const handleCloseOwnershipModal = () => {
    setShowOwnershipModal(false);
  };

  const handleUpdate = () => {
    refetchCampaigns();
    handleCloseEditCampaignModal();
  };

  const handleUpdateEventMarketing = () => {
    handleCloseEditCampaignModal();
  };

  // Approval fails without a brand signatory, so the admin picks one first
  const needsAgreementSignatory =
    requiresAgreementSignatory(data) && !data.agreementSignatoryId;

  const handleApproveCampaign = () => {
    if (needsAgreementSignatory) {
      setShowSignatoryModal(true);
      return;
    }
    if (data.type === CampaignType.SocialBoost) {
      setPricePerShare(null);
      setShowPricePerShareModal(true);
      return;
    }
    confirmApproveCampaign();
  };

  const confirmApproveCampaign = async (pps?: number | null) => {
    try {
      if (data.type === CampaignType.SocialBoost) {
        await approveSocialBoostCampaign({
          variables: {
            input: {
              id: data.id,
              pricePerShare: pps ?? 0,
              lockEnabled: true,
            },
          },
        });
      } else if (data.type === CampaignType.DirectBooking) {
        await approveDirectBookingCampaign({
          variables: {
            input: {
              id: data.id,
              lockEnabled: true,
            },
          },
        });
      } else {
        await approveCampaign({
          variables: {
            input: {
              id: data.id,
              lockEnabled: true,
            },
          },
        });
      }
      successNotification('Campaign is approved');
    } catch (error) {
      errorNotification(humanizeDatesInMessage((error as Error)?.message));
      console.log('approve campaign error:', { error });
    }
  };

  const handleApproveSocialBoost = async () => {
    if (!pricePerShare || pricePerShare <= 0) {
      errorNotification('Price per share must be greater than 0');
      return;
    }
    setShowPricePerShareModal(false);
    await confirmApproveCampaign(pricePerShare);
  };

  const handleDeclineCampaign = () => {
    let returnToAccountBalance = false;

    confirm({
      title: 'Are you sure you want to decline this campaign?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      content: (
        <Checkbox
          onChange={(e) => {
            returnToAccountBalance = e.target.checked;
          }}
        >
          Credit remaining budget &amp; paid launch fees to the brand&apos;s
          Account Balance instead of refunding to source
        </Checkbox>
      ),
      onOk: async () => {
        try {
          await declineCampaign({
            variables: {
              input: { id: data.id, returnToAccountBalance },
            },
          });
          successNotification('Campaign is declined');
        } catch (error) {
          errorNotification((error as Error)?.message);
          console.log('decline campaign error:', { error });
        }
      },
    });
  };

  const handleCompleteCampaign = async () => {
    confirm({
      title: 'Are you sure you want to complete this campaign?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await completeCampaign({
            variables: {
              input: { id: data.id },
            },
          });
          successNotification('Campaign is completed');
        } catch (error) {
          errorNotification((error as Error)?.message);
          console.log('complete campaign error:', { error });
        }
      },
    });
  };

  const handleDeleteCampaign = async () => {
    confirm({
      title: 'Are you sure you want to delete this campaign?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteCampaign({
            variables: {
              input: { campaignId: data.id },
            },
          });
          successNotification('Campaign is Deleted');
          refetchCampaigns();
        } catch (error) {
          errorNotification((error as Error)?.message);
        }
      },
    });
  };

  const handleWaiveFee = async () => {
    try {
      await editCampaignSettings({
        variables: {
          input: { campaignId: data.id, launchFee: 0 },
        },
      });
      successNotification('Campaign launch fee waived');
      refetchCampaigns();
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.log('edit campaign settings error:', { error });
    }
  };

  const handleUnwaiveFee = async () => {
    try {
      await unwaiveLaunchFee({
        variables: {
          input: { campaignId: data.id },
        },
      });
      successNotification('Campaign launch fee unwaived');
      refetchCampaigns();
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.log('unwaive launch fee error:', { error });
    }
  };

  const handleDuplicateCampaign = () => {
    confirm({
      title: 'Are you sure you want to duplicate this campaign?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'default',
      cancelText: 'No',
      onOk: async () => {
        try {
          await duplicateCampaign({
            variables: {
              input: { campaignId: data.id, ownerId: data?.agency?.id },
            },
          });
          successNotification('Campaign duplicated successfully');
        } catch (error) {
          errorNotification((error as Error)?.message);
          console.log('duplicate campaign error:', { error });
        }
      },
    });
  };

  const handleOpenEditDeadlineModal = async () => {
    try {
      await takeCampaignLock({
        variables: {
          input: {
            id: data.id,
          },
        },
      });

      successNotification('Lock success!');
      setShowEditDeadlineModal(true);
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Error locking campaign:', error);
    }
  };

  const handleCloseDeadlineModal = async () => {
    refetchCampaigns();
    setShowEditDeadlineModal(false);
    setModalKey(Date.now().toString());

    try {
      await releaseCampaignLock({
        variables: {
          input: {
            id: data.id,
          },
        },
      });

      successNotification('Unlock success!');
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Error unlocking campaign:', error);
    }
  };

  const isLoading =
    loadingApprove ||
    loadingApproveDirectBooking ||
    loadingApproveSocialBoost ||
    loadingDecline ||
    loadingComplete ||
    loadingEditCampaignSettings ||
    loadingUnwaiveLaunchFee ||
    loadingDuplicate;

  // if campaign is on review or only had details filled, we can approve, edit and reject
  const showExtraActions =
    data.status === CampaignStatus.ON_REVIEW ||
    data.status === CampaignStatus.DETAIL_FILLED;

  const showSocialEdit = data.type === CampaignType.Social;
  const showSocialBoostEdit = data.type === CampaignType.SocialBoost;
  const showSocialShareEdit = data.type === CampaignType.SocialShare;
  const showBookMeEdit = data.type === CampaignType.BookMe;
  const showSponsoredEdit = data.type === CampaignType.SponsoredAppearance;
  const isEventMarketing = data.type === CampaignType.EventMarketing;
  const showUgcEdit = data.type === CampaignType.Ugc;
  const showCustomEdit =
    data.type === CampaignType.Custom ||
    data.type === CampaignType.DirectBooking;

  const isCompleted = data?.status === CampaignStatus.COMPLETED;
  const canWaiveFee =
    data?.status === CampaignStatus.DETAIL_FILLED &&
    !isEventMarketing &&
    !!data.launchFee;

  const isDraft = data?.status === CampaignStatus.DETAIL_FILLED;

  const canUnwaiveFee =
    data?.status === CampaignStatus.DETAIL_FILLED &&
    !isEventMarketing &&
    !data.launchFee;
  const showDeleteCampaignBtn =
    data?.status === CampaignStatus.DETAIL_FILLED ||
    data?.status === CampaignStatus.DECLINED;

  const showEditDeadline =
    data?.status === CampaignStatus.PAID_AND_ACTIVE &&
    data?.applicationDeadlineDays;

  const isCampaignLocked = Boolean(campaignLock?.getCampaignLock);

  const { userEmail, userFirstName, userLastName } =
    campaignLock?.getCampaignLock ?? {};

  const lockHolder =
    userFirstName && userLastName && userEmail ? (
      <>
        <LockOutlined />
        <span>
          <a href={`mailto:${userEmail}`} onClick={(e) => e.stopPropagation()}>
            {userFirstName} {userLastName[0]}.
          </a>{' '}
          {showEditDeadline
            ? 'has deadline changes pending'
            : 'has a draft with unsaved changes'}
        </span>
      </>
    ) : null;

  // The record's actions are a menu, not a column of buttons: one row per
  // action, grouped by what each does to the campaign, affirmative ones in
  // green and irreversible ones last and in red.
  const campaignActions: RowMenuAction[] = [
    {
      key: 'details',
      label: 'View details',
      icon: <ProfileOutlined />,
      onClick: isEventMarketing
        ? handleOpenDetailsModal
        : handleOpenCampaignDetails,
    },
    {
      key: 'edit',
      label: 'Edit campaign',
      icon: <EditOutlined />,
      hidden: !showExtraActions,
      disabled: isLoading || isCampaignLocked,
      onClick: handleOpenEditCampaignModal,
    },
    {
      key: 'edit-deadlines',
      label: 'Edit deadlines',
      icon: <CalendarOutlined />,
      hidden: !showEditDeadline,
      disabled: isLoading || isCampaignLocked,
      onClick: handleOpenEditDeadlineModal,
    },
    {
      key: 'release-lock',
      label: 'Release edit lock',
      icon: <UnlockOutlined />,
      hidden: !isCampaignLocked,
      disabled: isLoading,
      onClick: handleReleaseCampaignLock,
    },
    {
      key: 'approve',
      label: needsAgreementSignatory ? (
        <Tooltip
          placement="left"
          title="This campaign requires the digital agreement and has no brand signatory yet. You'll choose who signs for the brand before it's approved."
        >
          Approve…
        </Tooltip>
      ) : (
        'Approve'
      ),
      icon: <CheckCircleOutlined />,
      positive: true,
      startsGroup: true,
      hidden: !showExtraActions,
      disabled: isLoading,
      onClick: handleApproveCampaign,
    },
    {
      key: 'decline',
      label: 'Decline',
      icon: <CloseCircleOutlined />,
      danger: true,
      hidden: !showExtraActions,
      disabled: isLoading,
      onClick: handleDeclineCampaign,
    },
    {
      key: 'waive-fee',
      label: 'Waive launch fee',
      icon: <DollarOutlined />,
      startsGroup: true,
      hidden: !canWaiveFee,
      disabled: isLoading,
      onClick: handleWaiveFee,
    },
    {
      key: 'unwaive-fee',
      label: 'Unwaive launch fee',
      icon: <DollarOutlined />,
      startsGroup: true,
      hidden: !canUnwaiveFee,
      disabled: isLoading,
      onClick: handleUnwaiveFee,
    },
    {
      key: 'change-owner',
      label: 'Change owner',
      icon: <UserSwitchOutlined />,
      startsGroup: true,
      onClick: handleOpenOwnershipModal,
    },
    {
      key: 'duplicate',
      label: 'Duplicate campaign',
      icon: <CopyOutlined />,
      disabled: isLoading,
      onClick: handleDuplicateCampaign,
    },
    {
      key: 'complete',
      label: 'Mark as complete',
      icon: <FlagOutlined />,
      hidden: isCompleted,
      disabled: isLoading,
      onClick: handleCompleteCampaign,
    },
    {
      key: 'view-campaign',
      label: 'View campaign',
      icon: <ExportOutlined />,
      href: `${getEnvVariables.CAMPAIGN_APP_URL}/${data.id}`,
    },
    {
      key: 'delete',
      label: `Delete ${isDraft ? 'draft' : 'campaign'}`,
      icon: <DeleteOutlined />,
      danger: true,
      startsGroup: true,
      hidden: !showDeleteCampaignBtn,
      disabled: isLoading,
      onClick: handleDeleteCampaign,
    },
  ];

  return (
    <>
      <RowActionsMenu
        label="Campaign actions"
        actions={campaignActions}
        notice={lockHolder}
        onOpen={() => {
          try {
            getCampaignLock({
              variables: {
                input: {
                  id: data?.id,
                },
              },
            });
          } catch (error) {
            errorNotification((error as Error)?.message);
            console.error('Error getting campaign lock:', { error });
          }
        }}
      />

      <Modal
        title="Campaign details"
        visible={showDetailsModal}
        footer={null}
        onCancel={handleCloseDetailsModal}
        width="90vw"
      >
        <EventMarketingDetailsModal record={data} />
      </Modal>

      <Modal
        title="Edit Campaign"
        visible={showEditModal}
        footer={null}
        onCancel={handleCloseEditCampaignModal}
        width={1120}
      >
        {showSocialEdit && (
          <SocialCampaignsEditModal
            key={`social-edit-${modalKey}`}
            record={data}
            onFinish={handleUpdate}
          />
        )}

        {showSocialBoostEdit && (
          <SocialBoostCampaignEditModal
            key={`social-boost-edit-${modalKey}`}
            record={data}
            onFinish={handleUpdate}
          />
        )}

        {showSocialShareEdit && (
          <SocialShareCampaignEditModal
            key={`social-share-edit-${modalKey}`}
            record={data}
            onFinish={handleUpdate}
          />
        )}

        {showBookMeEdit && (
          <BookMeCampaignsEditModal
            key={`bookme-edit-${modalKey}`}
            record={data}
            onFinish={handleUpdate}
          />
        )}

        {showSponsoredEdit && (
          <SponsoredAppearanceCampaignEditModal
            key={`sponsored-edit-${modalKey}`}
            record={data}
            onFinish={handleUpdate}
          />
        )}

        {isEventMarketing && (
          <EventMarketingEditModal
            key={`event-marketing-edit-${modalKey}`}
            record={data}
            onFinish={handleUpdateEventMarketing}
          />
        )}

        {showUgcEdit && (
          <UgcCampaignsEditModal
            key={`ugc-edit-${modalKey}`}
            record={data}
            onFinish={handleUpdate}
          />
        )}

        {showEditModal && showCustomEdit && (
          <CustomCampaignsEditModal
            key={`custom-edit-${modalKey}`}
            record={data}
            onFinish={handleUpdate}
            isOpen={showEditModal}
          />
        )}
      </Modal>

      <Modal
        title="Edit Deadlines"
        visible={showEditDeadlineModal}
        footer={null}
        onCancel={handleCloseDeadlineModal}
        width={720}
      >
        <EditDeadlineModal
          key={`edit-deadline-${modalKey}`}
          record={data}
          onFinish={handleCloseDeadlineModal}
        />
      </Modal>

      <Modal
        title="Change Campaign Owner"
        visible={showOwnershipModal}
        footer={null}
        onCancel={handleCloseOwnershipModal}
        width={970}
      >
        <ChangeOwnershipModal
          record={data}
          onFinish={handleCloseOwnershipModal}
        />
      </Modal>

      <ApproveWithSignatoryModal
        visible={showSignatoryModal}
        campaignId={data.id}
        ownerId={data.agency?.id}
        approving={loadingApprove || loadingApproveDirectBooking}
        onCancel={() => setShowSignatoryModal(false)}
        onApprove={async () => {
          await confirmApproveCampaign();
          setShowSignatoryModal(false);
        }}
      />

      <Modal
        title="Approve Social Boost Campaign"
        visible={showPricePerShareModal}
        onOk={handleApproveSocialBoost}
        onCancel={() => setShowPricePerShareModal(false)}
        okText="Approve"
        confirmLoading={
          loadingApprove ||
          loadingApproveDirectBooking ||
          loadingApproveSocialBoost
        }
      >
        <p>Enter the talent-side price per share:</p>
        <InputNumber
          min={1}
          precision={2}
          value={pricePerShare ?? undefined}
          onChange={(val) => setPricePerShare(val)}
          style={{ width: '100%' }}
          placeholder="e.g. 25"
        />
      </Modal>
    </>
  );
};

export default CampaignsActions;
